import React, { useEffect, useState } from 'react'
import PageHeader from '../../shared/page-header/PageHeader'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import axios from 'axios'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getModulePermissions } from '../../utils/permissionHelper'
import { exportToExcel, exportToPDF } from '../../utils/downloadExportedFiles'
import FinanceRegisterModal from './FinanceRegisterModal'
import FinanceRegisterGrid from './FinanceRegisterGrid'
import { logAuditEvent } from '../../utils/auditLogger'

function FinanceRegisterMain(props) {
  const [financeRegisterList, setFinanceRegisterList] = useState([])
  const [modalIsOpen, setIsOpen] = React.useState(false)
  const [alert, setAlert] = useState(null)
  const [userModulePermission, setUserModulePermission] = React.useState([])
  const moduleName = props.Module || 'Finance Register'

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module)
    setUserModulePermission(modulePermissions ? modulePermissions : [])
    fetchFinanceRegisters()
  }, [props.Module])

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', `Accessed ${moduleName}`)
  }, [moduleName])

  function openAddModal() {
    setIsOpen(true)
    logAuditEvent(moduleName, 'Add Button Click', `Opened add ${moduleName} modal`)
  }

  function closeModal() {
    setIsOpen(false)
  }

  async function onSubmit(e, data) {
    if (e.target.name === 'Add') {
      const financeRegisterModel = data
      if (financeRegisterModel) {
        let financeRegisterDetails = {
          FinanceRegisterId: null,
          SalesId: financeRegisterModel.SalesId,
          CustomerName: financeRegisterModel.CustomerName || '',
          MobileNo: financeRegisterModel.MobileNo || '',
          EmailId: financeRegisterModel.EmailId || '',
          ChassisNo: financeRegisterModel.ChassisNo || '',
          EngineNo: financeRegisterModel.EngineNo || '',
          VehicleId: financeRegisterModel.VehicleId || null,
          VehicleCost: financeRegisterModel.VehicleCost || null,
          FinancePartnerId: financeRegisterModel.FinancePartnerId || null,
          DownPayment: financeRegisterModel.DownPayment || null,
          EMITenure: financeRegisterModel.EMITenure || null,
          ProcessingFees: financeRegisterModel.ProcessingFees || null,
          InterestRate: financeRegisterModel.InterestRate || null,
          EMIAmount: financeRegisterModel.EMIAmount || null,
          LoanApplicationNo: financeRegisterModel.LoanApplicationNo || '',
          StatusId: financeRegisterModel.StatusId || null,
          UserId: getStorageValues('userId')
        }
        if (Number(financeRegisterDetails.StatusId) === 4) {
          CreateAlert('Use the Status dropdown completion flow to mark as Completed (Loan Account Number + Next EMI Date required).', 'Warning', setAlert)
          return
        }
        if (validation(financeRegisterDetails)) {
          try {
            const response = await axios.post(
              getApiBaseUrl() + '/financeregister/save',
              financeRegisterDetails
            )
            if (response.status === 200) {
              if (response.data.data?.success === 1 || response.data.data?.success === undefined) {
                CreateAlert(response.data.data?.message || 'Finance register added successfully!!', 'Success', setAlert)
                fetchFinanceRegisters()
                closeModal()
              } else {
                CreateAlert(response.data.data?.message || 'Unable to save record', 'Error', setAlert)
              }
            }
          } catch (error) {
            console.log(error)
          }
        }
      } else {
        CreateAlert('Fields marked with an asterisk (*) are required!!', 'Error', setAlert)
      }
    }
  }

  function validation(financeRegisterDetails) {
    let isValid = true
    if (financeRegisterDetails) {
      if (!financeRegisterDetails.SalesId) {
        CreateAlert('Please select sales!!', 'Warning', setAlert)
        isValid = false
        return
      }
      const chassisValue = (financeRegisterDetails.ChassisNo || '').toString().trim()
      const engineValue = (financeRegisterDetails.EngineNo || '').toString().trim()
      if (!chassisValue || !engineValue) {
        CreateAlert('Please add chassis and engine number for selected sales!!', 'Warning', setAlert)
        isValid = false
        return
      }
      if (!financeRegisterDetails.FinancePartnerId) {
        CreateAlert('Please select finance partner!!', 'Warning', setAlert)
        isValid = false
        return
      }
      const emiValue = Number(financeRegisterDetails.EMIAmount)
      if (!financeRegisterDetails.EMIAmount || Number.isNaN(emiValue) || emiValue <= 0) {
        CreateAlert('Please enter EMI amount!!', 'Warning', setAlert)
        isValid = false
        return
      }
    } else {
      CreateAlert('Some problem fetching details entered by the user!!', 'Error', setAlert)
      isValid = false
      return
    }
    return isValid
  }

  const fetchFinanceRegisters = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + '/financeregister/getall', {
        params: { UserId: getStorageValues('userId') }
      })
      if (response.status === 200) {
        setFinanceRegisterList(response.data.data || [])
      }
    } catch (error) {
      console.log(error)
    }
  }

  const handleExportExcel = () => {
    const columns = [
      { name: 'Sales #', selector: 'SalesNumber' },
      { name: 'Customer Name', selector: 'CustomerName' },
      { name: 'Loan Application No', selector: 'LoanApplicationNo' },
      { name: 'Status', selector: 'Status' }
    ]
    exportToExcel('FinanceRegister', columns, financeRegisterList)
  }

  const handleExportPDF = () => {
    const columns = [
      { name: 'Sales #', selector: 'SalesNumber' },
      { name: 'Customer Name', selector: 'CustomerName' },
      { name: 'Loan Application No', selector: 'LoanApplicationNo' },
      { name: 'Status', selector: 'Status' }
    ]
    exportToPDF('FinanceRegister', columns, financeRegisterList)
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchFinanceRegisters}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <FinanceRegisterGrid
        FinanceRegisterData={financeRegisterList}
        fetchFinanceRegisters={fetchFinanceRegisters}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <FinanceRegisterModal
        show={modalIsOpen}
        UserAction={'Add'}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  )
}

export default FinanceRegisterMain